// =============================================================================
// WaterfallTab Component
// =============================================================================
// Displays waterfall analysis for a deal: capital summary, per-distribution
// tier cascade, per-investor allocation, and a verification check.
// =============================================================================

import React, { useState, useEffect } from 'react';
import { ArrowDownTrayIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  adminService,
  type WaterfallAnalysisData,
  type ShareClass,
  type ClawbackResult,
  type LookbackResult,
} from '../../services/admin.service';
import { getApiError } from '../../services/api';

// =============================================================================
// Props
// =============================================================================

interface WaterfallTabProps {
  dealId: string;
  shareClasses: ShareClass[];
  clawbackEnabled?: boolean;
  lookbackEnabled?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

const fmt = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const fmtExact = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const fmtPct = (value: number) => `${(value * 100).toFixed(2)}%`;

const fmtDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

const tierLabel = (tierNumber: number, tierName: string) => {
  if (tierNumber === 0) return 'Return of Capital';
  if (tierNumber === -1) return 'Preferred Return';
  if (tierNumber === -2) return 'GP Catch-Up';
  return tierName;
};

// =============================================================================
// Component
// =============================================================================

export default function WaterfallTab({ dealId, shareClasses, clawbackEnabled, lookbackEnabled }: WaterfallTabProps) {
  const [data, setData] = useState<WaterfallAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [expandedInvestor, setExpandedInvestor] = useState<string | null>(null);
  const [clawbackResult, setClawbackResult] = useState<ClawbackResult | null>(null);
  const [clawbackLoading, setClawbackLoading] = useState(false);
  const [lookbackResult, setLookbackResult] = useState<LookbackResult | null>(null);
  const [lookbackLoading, setLookbackLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    adminService
      .getWaterfallAnalysis(dealId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500">Loading waterfall analysis...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { capitalSummary, distributions, capitalEvents, investors } = data;

  // LP vs GP capital split ratios (used for capital call rows and totals)
  // Uses investorRole (LP/GP) to determine who is LP vs GP co-invest
  const totalInvested = investors.reduce((s, i) => s + i.invested, 0);
  const lpInvestedTotal = investors.filter(i => i.investorRole !== 'GP').reduce((s, i) => s + i.invested, 0);
  const gpInvestedTotal = investors.filter(i => i.investorRole === 'GP').reduce((s, i) => s + i.invested, 0);
  const lpRatio = totalInvested > 0 ? lpInvestedTotal / totalInvested : 1;
  const gpRatio = totalInvested > 0 ? gpInvestedTotal / totalInvested : 0;

  // Verification: sum of all investor distributions should equal total distributed
  const investorDistSum = investors.reduce((s, inv) => s + inv.totalDistributed, 0);
  const investorLpGpSum = investors.reduce((s, inv) => s + inv.lpShare + inv.gpPromote, 0);
  const verificationPassed =
    Math.abs(investorDistSum - capitalSummary.totalDistributed) < 0.02 &&
    Math.abs(investorLpGpSum - capitalSummary.totalDistributed) < 0.02;

  return (
    <div className="space-y-8">
      {/* ================================================================= */}
      {/* Section 1: Waterfall Structure Overview */}
      {/* ================================================================= */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Waterfall Structure</h3>
          <button
            onClick={async () => {
              setExporting(true);
              try {
                await adminService.exportDealWaterfall(dealId);
              } catch (err: any) {
                const errorMsg = err?.message || err?.response?.data?.error || 'Unknown error';
                alert(`Failed to export waterfall: ${errorMsg}`);
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting || shareClasses.length === 0}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export Waterfall'}
          </button>
        </div>
        {shareClasses.length === 0 ? (
          <p className="text-gray-500 italic">No share classes configured for this deal.</p>
        ) : (
          <div className="space-y-4">
            {shareClasses.map((sc) => (
              <div key={sc.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">
                    {sc.name} <span className="text-gray-400 text-sm">({sc.code})</span>
                  </h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      Priority {sc.priority}
                    </span>
                    {sc.maxEquityMultiple != null && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                        Capped at {sc.maxEquityMultiple}x
                      </span>
                    )}
                  </div>
                </div>
                {/* Tier flow */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Built-in stages: Preferred Return → Return of Capital */}
                  {sc.preferredReturnRate != null && sc.preferredReturnRate > 0 && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm">
                        <div className="font-medium text-blue-800">Preferred Return</div>
                        <div className="text-blue-600 text-xs">
                          {(sc.preferredReturnRate * 100).toFixed(1)}% ·{' '}
                          {sc.isCumulative ? 'Cumulative' : 'Non-cumulative'}
                        </div>
                      </div>
                      <span className="text-gray-300">&rarr;</span>
                    </>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm">
                    <div className="font-medium text-blue-800">Return of Capital</div>
                    <div className="text-blue-600 text-xs">100% LP</div>
                  </div>
                  <span className="text-gray-300">&rarr;</span>
                  {sc.catchUpPercent != null && sc.catchUpPercent > 0 && (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
                        <div className="font-medium text-amber-800">GP Catch-Up</div>
                        <div className="text-amber-600 text-xs">
                          {(sc.catchUpPercent * 100).toFixed(0)}% to GP
                        </div>
                      </div>
                      <span className="text-gray-300">&rarr;</span>
                    </>
                  )}
                  {/* Residual tiers */}
                  {(sc.waterfallTiers ?? [])
                    .filter((t) => t.tierNumber >= 1)
                    .sort((a, b) => a.tierNumber - b.tierNumber)
                    .map((tier, idx, arr) => (
                      <div key={tier.id} className="flex items-center gap-2">
                        <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-sm">
                          <div className="font-medium text-emerald-800">{tier.name}</div>
                          <div className="text-emerald-600 text-xs">
                            {tier.hurdleValue != null && (
                              <div>
                                {tier.hurdleType === 'EQUITY_MULTIPLE'
                                  ? `${tier.hurdleValue}x Hurdle`
                                  : `${(tier.hurdleValue * 100).toFixed(1)}% Hurdle`}
                              </div>
                            )}
                            <div>
                              LP {(tier.lpSplitPercent * 100).toFixed(0)}% / GP{' '}
                              {(tier.gpSplitPercent * 100).toFixed(0)}%
                            </div>
                            {tier.gpPromotePercent > 0 && (
                              <div>{(tier.gpPromotePercent * 100).toFixed(0)}% Promote</div>
                            )}
                          </div>
                        </div>
                        {idx < arr.length - 1 && <span className="text-gray-300">&rarr;</span>}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ================================================================= */}
      {/* Section 2: Capital Account Summary */}
      {/* ================================================================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Capital Account Summary</h3>

        {/* Capital */}
        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Capital</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard label="Total Committed" value={fmt(capitalSummary.totalCommitted ?? capitalSummary.totalEquity)} />
          <SummaryCard
            label="Total Called / Contributed"
            value={fmt(capitalSummary.totalContributed)}
            sub={
              (capitalSummary.totalCommitted ?? capitalSummary.totalEquity) > 0
                ? `${((capitalSummary.totalContributed / (capitalSummary.totalCommitted ?? capitalSummary.totalEquity)) * 100).toFixed(1)}% of commitment`
                : undefined
            }
          />
          <SummaryCard
            label="Total Unfunded Commitments"
            value={fmt(capitalSummary.unfundedCommitment ?? Math.max(0, capitalSummary.totalEquity - capitalSummary.totalContributed))}
          />
          <SummaryCard
            label="Total Capital Returned"
            value={fmt(capitalSummary.totalCapitalReturned)}
            sub={
              capitalSummary.totalContributed > 0
                ? `${((capitalSummary.totalCapitalReturned / capitalSummary.totalContributed) * 100).toFixed(1)}% returned`
                : undefined
            }
          />
          <SummaryCard label="Total Distributed Overall" value={fmt(capitalSummary.totalDistributed)} />
        </div>

        {/* Preferred Return */}
        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Preferred Return</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard label="Preferred Return Accrued" value={fmt(capitalSummary.prefReturnAccrued)} />
          <SummaryCard label="Preferred Return Paid" value={fmt(capitalSummary.prefReturnPaid)} />
        </div>

        {/* Return Summary */}
        {(() => {
          const lpInvestors = investors.filter(i => !i.isPromoteRecipient);
          const gpInvestors = investors.filter(i => i.isPromoteRecipient);

          const totalLpInvested = lpInvestors.reduce((s, i) => s + i.invested, 0);
          const totalLpDistributed = lpInvestors.reduce((s, i) => s + i.totalDistributed, 0);
          const lpEquityMultiple = totalLpInvested > 0 ? totalLpDistributed / totalLpInvested : 0;
          const lpIrr = lpInvestors.length > 0 ? lpInvestors[0].irr : null;

          const totalGpInvested = gpInvestors.reduce((s, i) => s + i.invested, 0);
          const totalGpDistributed = gpInvestors.reduce((s, i) => s + i.totalDistributed, 0);
          const gpEquityMultiple = totalGpInvested > 0 ? totalGpDistributed / totalGpInvested : 0;
          const gpIrr = gpInvestors.length > 0 ? gpInvestors[0].irr : null;

          const totalGpPromote = investors.reduce((s, i) => s + i.gpPromote, 0);

          return (
            <>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Return Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard
                  label="Total Deal IRR"
                  value={capitalSummary.dealIrr != null ? fmtPct(capitalSummary.dealIrr) : 'N/A'}
                />
                <SummaryCard
                  label="Total Deal Equity Multiple"
                  value={`${capitalSummary.equityMultiple.toFixed(2)}x`}
                />
                <SummaryCard
                  label="Total LP IRR"
                  value={lpIrr != null ? fmtPct(lpIrr) : 'N/A'}
                />
                <SummaryCard
                  label="Total LP Equity Multiple"
                  value={`${lpEquityMultiple.toFixed(2)}x`}
                />
                <SummaryCard
                  label="Total GP IRR"
                  value={gpIrr != null ? fmtPct(gpIrr) : 'N/A'}
                />
                <SummaryCard
                  label="Total GP Equity Multiple"
                  value={`${gpEquityMultiple.toFixed(2)}x`}
                />
                <SummaryCard
                  label="Total GP Promoted Interest Paid"
                  value={fmtExact(totalGpPromote)}
                />
              </div>
            </>
          );
        })()}
      </section>

      {/* ================================================================= */}
      {/* Preferred Equity Summary (only shown if tranches exist) */}
      {/* ================================================================= */}
      {data?.prefEquitySummary && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferred Equity</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <div className="text-xs text-gray-500">Total Invested</div>
              <div className="text-sm font-semibold text-gray-900">{fmt(data.prefEquitySummary.totalInvested)}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <div className="text-xs text-gray-500">Outstanding Balance</div>
              <div className="text-sm font-semibold text-purple-700">{fmt(data.prefEquitySummary.totalOutstanding)}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <div className="text-xs text-gray-500">Total Paid</div>
              <div className="text-sm font-semibold text-gray-900">{fmt(data.prefEquitySummary.totalPaid)}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <div className="text-xs text-gray-500">Active Tranches</div>
              <div className="text-sm font-semibold text-gray-900">{data.prefEquitySummary.trancheCount}</div>
            </div>
          </div>
          {data.prefEquitySummary.tranches.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Name</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Invested</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Rate</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Compounding</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Outstanding</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Total Paid</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.prefEquitySummary.tranches.map(t => (
                    <tr key={t.id}>
                      <td className="px-4 py-2 font-medium text-gray-900">{t.name}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{fmt(t.investedAmount)}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{(t.preferredReturnRate * 100).toFixed(2)}%</td>
                      <td className="px-4 py-2 text-gray-700">{t.compoundingMethod}</td>
                      <td className="px-4 py-2 text-right text-purple-700 font-medium">{fmt(t.outstandingBalance)}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{fmt(t.totalPaid)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${t.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {t.isActive ? 'Active' : 'Repaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ================================================================= */}
      {/* Fee Summary (only shown if fees exist) */}
      {/* ================================================================= */}
      {data?.feeSummary && data.feeSummary.totalAllFees > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.feeSummary.totalAssetManagement > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Asset Management Fees</div>
                <div className="text-sm font-semibold text-gray-900">{fmt(data.feeSummary.totalAssetManagement)}</div>
              </div>
            )}
            {data.feeSummary.totalDisposition > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Disposition Fees</div>
                <div className="text-sm font-semibold text-gray-900">{fmt(data.feeSummary.totalDisposition)}</div>
              </div>
            )}
            {data.feeSummary.totalAcquisition > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Acquisition Fees</div>
                <div className="text-sm font-semibold text-gray-900">{fmt(data.feeSummary.totalAcquisition)}</div>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Total Fees Paid</div>
              <div className="text-sm font-semibold text-gray-900">{fmt(data.feeSummary.totalAllFees)}</div>
            </div>
            {data.feeSummary.totalOffsets > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Fee Offsets Applied</div>
                <div className="text-sm font-semibold text-green-700">({fmt(data.feeSummary.totalOffsets)})</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* Escrow Summary (only shown if escrow is enabled) */}
      {/* ================================================================= */}
      {data?.escrowSummary && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Escrow Holdback</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="text-xs text-gray-500">Escrow Rate</div>
              <div className="text-sm font-semibold text-gray-900">{(data.escrowSummary.escrowPercent * 100).toFixed(0)}% of GP Promote</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="text-xs text-gray-500">Total Escrowed</div>
              <div className="text-sm font-semibold text-gray-900">{fmt(data.escrowSummary.totalEscrowed)}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="text-xs text-gray-500">Current Balance (Held)</div>
              <div className="text-sm font-semibold text-amber-700">{fmt(data.escrowSummary.currentBalance)}</div>
            </div>
            {data.escrowSummary.totalReleased > 0 && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="text-xs text-gray-500">Released to GP</div>
                <div className="text-sm font-semibold text-green-700">{fmt(data.escrowSummary.totalReleased)}</div>
              </div>
            )}
            {data.escrowSummary.totalClawedBack > 0 && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <div className="text-xs text-gray-500">Clawed Back to LPs</div>
                <div className="text-sm font-semibold text-red-700">{fmt(data.escrowSummary.totalClawedBack)}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* Clawback Status (only shown when clawbackEnabled) */}
      {/* ================================================================= */}
      {clawbackEnabled && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Clawback Status</h3>
          {clawbackResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Total GP Promote Received</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(clawbackResult.totalGpPromote)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">LP Actual IRR</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {clawbackResult.lpActualIrr != null ? fmtPct(clawbackResult.lpActualIrr) : 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">LP Target IRR (Pref Rate)</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {clawbackResult.lpTargetIrr > 0 ? fmtPct(clawbackResult.lpTargetIrr) : 'N/A'}
                  </div>
                </div>
                <div className={`rounded-lg p-3 ${clawbackResult.clawbackAmount > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="text-xs text-gray-500">Clawback Amount</div>
                  <div className={`text-sm font-semibold ${clawbackResult.clawbackAmount > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {clawbackResult.clawbackAmount > 0 ? fmt(clawbackResult.clawbackAmount) : 'None'}
                  </div>
                </div>
              </div>
              {clawbackResult.clawbackAmount > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <div className="text-xs text-gray-500">Escrow Available</div>
                    <div className="text-sm font-semibold text-gray-900">{fmt(clawbackResult.escrowAvailable)}</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <div className="text-xs text-gray-500">Escrow Used for Clawback</div>
                    <div className="text-sm font-semibold text-gray-900">{fmt(clawbackResult.escrowUsed)}</div>
                  </div>
                  <div className={`rounded-lg p-3 ${clawbackResult.remainingGpObligation > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="text-xs text-gray-500">Remaining GP Obligation</div>
                    <div className={`text-sm font-semibold ${clawbackResult.remainingGpObligation > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {clawbackResult.remainingGpObligation > 0 ? fmt(clawbackResult.remainingGpObligation) : '$0.00'}
                    </div>
                  </div>
                </div>
              )}
              {clawbackResult.isFinal && (
                <p className="text-xs text-gray-500 italic">This is a finalized clawback calculation.</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">No clawback calculation has been run yet.</p>
              <button
                onClick={async () => {
                  setClawbackLoading(true);
                  try {
                    const result = await adminService.simulateClawback(dealId);
                    setClawbackResult(result);
                  } catch {
                    // Silently fail
                  } finally {
                    setClawbackLoading(false);
                  }
                }}
                disabled={clawbackLoading}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {clawbackLoading ? 'Calculating...' : 'Simulate Clawback'}
              </button>
            </div>
          )}
        </section>
      )}

      {/* ================================================================= */}
      {/* Lookback Analysis (only shown when lookbackEnabled) */}
      {/* ================================================================= */}
      {lookbackEnabled && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lookback / True-Up Analysis</h3>
          {lookbackResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Total Distributed</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(lookbackResult.totalDistributed)}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="text-xs text-gray-500">GP Actual Total</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(lookbackResult.gpActualTotal)}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="text-xs text-gray-500">GP Hypothetical (Lump-Sum)</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(lookbackResult.gpHypotheticalTotal)}</div>
                </div>
                <div className={`rounded-lg p-3 ${lookbackResult.gpAdjustment > 0 ? 'bg-red-50 border border-red-200' : lookbackResult.gpAdjustment < 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="text-xs text-gray-500">GP Adjustment</div>
                  <div className={`text-sm font-semibold ${lookbackResult.gpAdjustment > 0 ? 'text-red-700' : lookbackResult.gpAdjustment < 0 ? 'text-green-700' : 'text-gray-700'}`}>
                    {lookbackResult.gpAdjustment > 0 ? `+${fmt(lookbackResult.gpAdjustment)} (over-received)` : lookbackResult.gpAdjustment < 0 ? `${fmt(lookbackResult.gpAdjustment)} (under-received)` : 'No adjustment'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <div className="text-xs text-gray-500">LP Actual Total</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(lookbackResult.lpActualTotal)}</div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <div className="text-xs text-gray-500">LP Hypothetical (Lump-Sum)</div>
                  <div className="text-sm font-semibold text-gray-900">{fmt(lookbackResult.lpHypotheticalTotal)}</div>
                </div>
                <div className={`rounded-lg p-3 ${lookbackResult.lpAdjustment > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="text-xs text-gray-500">LP Adjustment</div>
                  <div className={`text-sm font-semibold ${lookbackResult.lpAdjustment > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                    {lookbackResult.lpAdjustment > 0 ? `+${fmt(lookbackResult.lpAdjustment)} (owed to LP)` : 'No adjustment'}
                  </div>
                </div>
              </div>
              {lookbackResult.isFinal && (
                <p className="text-xs text-gray-500 italic">This is a finalized lookback calculation.</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">No lookback analysis has been run yet.</p>
              <button
                onClick={async () => {
                  setLookbackLoading(true);
                  try {
                    const result = await adminService.simulateLookback(dealId);
                    setLookbackResult(result);
                  } catch {
                    // Silently fail
                  } finally {
                    setLookbackLoading(false);
                  }
                }}
                disabled={lookbackLoading}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {lookbackLoading ? 'Calculating...' : 'Simulate Lookback'}
              </button>
            </div>
          )}
        </section>
      )}

      {/* ================================================================= */}
      {/* Section 3: Distribution Waterfall Detail */}
      {/* ================================================================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution Waterfall</h3>
        {distributions.length === 0 && (!capitalEvents || capitalEvents.filter(e => e.eventType === 'CAPITAL_CALL').length === 0) ? (
          <p className="text-gray-500 italic">No published distributions yet.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Tier</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Amount</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">LP</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">GP Co-Invest</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Promote</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Remaining CF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Capital call rows (from capitalEvents if available) */}
                {capitalEvents?.filter(e => e.eventType === 'CAPITAL_CALL').map((evt) => (
                  <React.Fragment key={evt.id}>
                    <tr className="bg-red-50">
                      <td className="px-4 py-2 font-medium text-red-800">
                        {fmtDate(evt.date)} — Capital Call
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-red-700">
                        ({fmtExact(evt.amount)})
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-red-700">
                        ({fmtExact(evt.amount * lpRatio)})
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-red-700">
                        ({fmtExact(evt.amount * gpRatio)})
                      </td>
                      <td className="px-4 py-2 text-right text-gray-400">—</td>
                      <td className="px-4 py-2 text-right font-medium text-red-700">
                        ({fmtExact(evt.amount)})
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                {/* Distribution rows (original format from screenshot) */}
                {distributions.map((dist) => (
                  <React.Fragment key={dist.id}>
                    {/* Distribution header row */}
                    <tr className="bg-blue-50">
                      <td colSpan={2} className="px-4 py-2 font-medium text-blue-800">
                        {fmtDate(dist.date)} — {dist.type.replace(/_/g, ' ')}
                      </td>
                      <td colSpan={4} className="px-4 py-2 text-right font-medium text-blue-800">
                        {fmt(dist.totalAmount)}
                      </td>
                    </tr>
                    {/* Tier rows */}
                    {dist.tiers.map((tier) => (
                      <tr key={`${dist.id}-${tier.tierNumber}`} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-700 pl-8">
                          {tierLabel(tier.tierNumber, tier.tierName)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.amountIn)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.lpAmount)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.gpAmount)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {tier.promoteAmount > 0.005 ? fmtExact(tier.promoteAmount) : '$0.00'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-500">
                          {fmtExact(tier.remainingCf)}
                        </td>
                      </tr>
                    ))}
                    {/* Total row for distribution */}
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-4 py-2 text-gray-700 pl-8">TOTAL</td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {fmtExact(dist.tiers.reduce((s, t) => s + t.amountIn, 0))}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {fmtExact(dist.tiers.reduce((s, t) => s + t.lpAmount, 0))}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {fmtExact(dist.tiers.reduce((s, t) => s + t.gpAmount, 0))}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {fmtExact(dist.tiers.reduce((s, t) => s + (t.promoteAmount || 0), 0))}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">—</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Waterfall Totals — aggregates from distributions array (always available) */}
        {distributions.length > 0 && (() => {
          // Aggregate all tiers across all distributions
          const tierTotals = new Map<number, { tierName: string; amountIn: number; lpAmount: number; gpAmount: number; promoteAmount: number }>();
          let grandDistAmount = 0;

          for (const dist of distributions) {
            grandDistAmount += dist.totalAmount;
            for (const tier of dist.tiers) {
              const tierPromoteAmt = tier.promoteAmount || 0;
              const existing = tierTotals.get(tier.tierNumber);
              if (existing) {
                existing.amountIn += tier.amountIn;
                existing.lpAmount += tier.lpAmount;
                existing.gpAmount += tier.gpAmount;
                existing.promoteAmount += tierPromoteAmt;
              } else {
                tierTotals.set(tier.tierNumber, {
                  tierName: tier.tierName,
                  amountIn: tier.amountIn,
                  lpAmount: tier.lpAmount,
                  gpAmount: tier.gpAmount,
                  promoteAmount: tierPromoteAmt,
                });
              }
            }
          }

          // Capital called: use top-level LP/GP invested totals
          const grandCallAmount = capitalEvents && capitalEvents.length > 0
            ? capitalEvents.filter(e => e.eventType === 'CAPITAL_CALL').reduce((s, e) => s + e.amount, 0)
            : capitalSummary.totalContributed;

          // Sort tiers in waterfall cascade order
          const waterfallOrder = (n: number) => n === -1 ? 0 : n === 0 ? 1 : n === -2 ? 2 : n + 2;
          const sortedTiers = [...tierTotals.entries()].sort((a, b) => waterfallOrder(a[0]) - waterfallOrder(b[0]));
          const grandLp = sortedTiers.reduce((s, [, t]) => s + t.lpAmount, 0);
          const grandGp = sortedTiers.reduce((s, [, t]) => s + t.gpAmount, 0);
          const grandPromote = sortedTiers.reduce((s, [, t]) => s + t.promoteAmount, 0);
          const netCashFlow = grandDistAmount - grandCallAmount;

          return (
            <div className="mt-6">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Waterfall Totals</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Category</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">LP</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">GP Co-Invest</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Promote</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {/* Capital calls total */}
                    <tr className="bg-red-50/50">
                      <td className="px-4 py-2 font-medium text-red-800">Total Capital Called</td>
                      <td className="px-4 py-2 text-right font-medium text-red-700">
                        ({fmtExact(grandCallAmount)})
                      </td>
                      <td className="px-4 py-2 text-right text-red-700">
                        ({fmtExact(lpInvestedTotal)})
                      </td>
                      <td className="px-4 py-2 text-right text-red-700">
                        ({fmtExact(gpInvestedTotal)})
                      </td>
                      <td className="px-4 py-2 text-right text-gray-400">—</td>
                    </tr>
                    {/* Per-tier distribution totals */}
                    {sortedTiers.map(([tierNumber, tier]) => (
                      <tr key={tierNumber} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-700 pl-6">
                          {tierLabel(tierNumber, tier.tierName)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.amountIn)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.lpAmount)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmtExact(tier.gpAmount)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {tier.promoteAmount > 0.005 ? fmtExact(tier.promoteAmount) : '$0.00'}
                        </td>
                      </tr>
                    ))}
                    {/* Total distributions */}
                    <tr className="bg-green-50/50 font-medium">
                      <td className="px-4 py-2 text-green-800">Total Distributed</td>
                      <td className="px-4 py-2 text-right text-green-700">
                        {fmtExact(grandDistAmount)}
                      </td>
                      <td className="px-4 py-2 text-right text-green-700">
                        {fmtExact(grandLp)}
                      </td>
                      <td className="px-4 py-2 text-right text-green-700">
                        {fmtExact(grandGp)}
                      </td>
                      <td className="px-4 py-2 text-right text-green-700">
                        {fmtExact(grandPromote)}
                      </td>
                    </tr>
                    {/* Net cash flow */}
                    <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                      <td className="px-4 py-2.5 text-gray-900">Net Cash Flow</td>
                      <td className={`px-4 py-2.5 text-right ${netCashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {netCashFlow >= 0 ? fmtExact(netCashFlow) : `(${fmtExact(Math.abs(netCashFlow))})`}
                      </td>
                      <td className={`px-4 py-2.5 text-right ${grandLp - lpInvestedTotal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {grandLp - lpInvestedTotal >= 0
                          ? fmtExact(grandLp - lpInvestedTotal)
                          : `(${fmtExact(Math.abs(grandLp - lpInvestedTotal))})`}
                      </td>
                      <td className={`px-4 py-2.5 text-right ${grandGp - gpInvestedTotal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {grandGp - gpInvestedTotal >= 0
                          ? fmtExact(grandGp - gpInvestedTotal)
                          : `(${fmtExact(Math.abs(grandGp - gpInvestedTotal))})`}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-400">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </section>

      {/* ================================================================= */}
      {/* Section 4: Per-Investor Allocation Summary */}
      {/* ================================================================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Allocations</h3>
        {investors.length === 0 ? (
          <p className="text-gray-500 italic">No active investments.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-8"></th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Investor</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Class</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Invested</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Distributed</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Pro-Rata Share</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Promote</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Multiple</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">IRR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {investors.map((inv) => {
                  const isExpanded = expandedInvestor === inv.investmentId;
                  return (
                    <>
                      {/* Summary row */}
                      <tr
                        key={inv.investmentId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() =>
                          setExpandedInvestor(isExpanded ? null : inv.investmentId)
                        }
                      >
                        <td className="px-4 py-2 text-gray-400">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="text-gray-900">
                            {inv.investorName}
                            {inv.investorRole === 'GP' && (
                              <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                GP
                              </span>
                            )}
                            {inv.isPromoteRecipient && (
                              <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                Promote
                              </span>
                            )}
                          </div>
                          {inv.entityName && (
                            <div className="text-gray-500 text-xs">{inv.entityName}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {inv.shareClassName ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmt(inv.invested)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmt(inv.totalDistributed)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {fmt(inv.lpShare)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {inv.gpPromote > 0 ? fmt(inv.gpPromote) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {inv.equityMultiple.toFixed(2)}x
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {inv.irr != null ? fmtPct(inv.irr) : '—'}
                        </td>
                      </tr>
                      {/* Expanded detail */}
                      {isExpanded && (
                        <tr key={`detail-${inv.investmentId}`}>
                          <td colSpan={9} className="px-0 py-0">
                            <div className="bg-gray-50 border-t border-b border-gray-200 px-8 py-4">
                              {/* Metrics row */}
                              <div className="grid grid-cols-4 gap-4 mb-4">
                                <div className="bg-white border border-gray-200 rounded p-3">
                                  <p className="text-xs text-gray-500">Total Invested</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {fmtExact(inv.invested)}
                                  </p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-3">
                                  <p className="text-xs text-gray-500">Total Distributed</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {fmtExact(inv.totalDistributed)}
                                  </p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-3">
                                  <p className="text-xs text-gray-500">Equity Multiple</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {inv.equityMultiple.toFixed(2)}x
                                  </p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-3">
                                  <p className="text-xs text-gray-500">IRR</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {inv.irr != null ? fmtPct(inv.irr) : 'N/A'}
                                  </p>
                                </div>
                              </div>
                              {/* Cash flow detail table */}
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="py-2 text-left font-medium text-gray-500">
                                      Date
                                    </th>
                                    <th className="py-2 text-left font-medium text-gray-500">
                                      Type
                                    </th>
                                    <th className="py-2 text-right font-medium text-gray-500">
                                      Amount
                                    </th>
                                    <th className="py-2 text-left font-medium text-gray-500 pl-4">
                                      Breakdown
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {inv.cashFlows.map((cf, idx) => (
                                    <tr
                                      key={idx}
                                      className="border-b border-gray-100"
                                    >
                                      <td className="py-1.5 text-gray-700">
                                        {fmtDate(cf.date)}
                                      </td>
                                      <td className="py-1.5">
                                        <span
                                          className={
                                            cf.type === 'contribution'
                                              ? 'text-red-700'
                                              : 'text-green-700'
                                          }
                                        >
                                          {cf.label}
                                        </span>
                                      </td>
                                      <td
                                        className={`py-1.5 text-right ${
                                          cf.amount < 0
                                            ? 'text-red-700'
                                            : 'text-green-700'
                                        }`}
                                      >
                                        {cf.amount < 0 ? `(${fmtExact(Math.abs(cf.amount))})` : fmtExact(cf.amount)}
                                      </td>
                                      <td className="py-1.5 pl-4">
                                        {cf.tierBreakdown && (
                                          <div className="flex flex-col gap-0.5">
                                            {cf.tierBreakdown.map((tb) => (
                                              <div key={tb.tierNumber} className="text-gray-700 text-sm">
                                                <span className="font-medium">{tierLabel(tb.tierNumber, tb.tierName)}:</span>{' '}
                                                LP {fmtExact(tb.lpAmount)}
                                                {tb.gpAmount > 0 && ` · GP ${fmtExact(tb.gpAmount)}`}
                                                {tb.promoteAmount > 0 && ` · Promote ${fmtExact(tb.promoteAmount)}`}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                  {/* Running balance footer */}
                                  <tr className="font-medium border-t border-gray-300">
                                    <td className="py-2 text-gray-700" colSpan={2}>
                                      Net Position
                                    </td>
                                    <td
                                      className={`py-2 text-right ${
                                        inv.totalDistributed - inv.invested >= 0
                                          ? 'text-green-700'
                                          : 'text-red-700'
                                      }`}
                                    >
                                      {fmtExact(inv.totalDistributed - inv.invested)}
                                    </td>
                                    <td className="py-2 pl-4 text-gray-500">
                                      LP: {fmtExact(inv.lpShare)}
                                      {inv.gpPromote > 0 &&
                                        ` · GP Promote: ${fmtExact(inv.gpPromote)}`}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {/* Totals row */}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2 text-gray-700">TOTALS</td>
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2 text-right text-gray-900">
                    {fmt(investors.reduce((s, inv) => s + inv.invested, 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">
                    {fmt(investorDistSum)}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">
                    {fmt(investors.reduce((s, inv) => s + inv.lpShare, 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">
                    {fmt(investors.reduce((s, inv) => s + inv.gpPromote, 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">
                    {capitalSummary.equityMultiple.toFixed(2)}x
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900">
                    {capitalSummary.dealIrr != null ? fmtPct(capitalSummary.dealIrr) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ================================================================= */}
      {/* Section 5: Verification Check */}
      {/* ================================================================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification</h3>
        <div
          className={`flex items-center gap-3 p-4 rounded-lg border ${
            verificationPassed
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          {verificationPassed ? (
            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
          )}
          <div>
            <p
              className={`font-medium ${
                verificationPassed ? 'text-green-800' : 'text-red-800'
              }`}
            >
              {verificationPassed
                ? 'All allocations verified'
                : 'Allocation mismatch detected'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Total Distributed: {fmtExact(capitalSummary.totalDistributed)} · Sum of Investor
              Allocations: {fmtExact(investorDistSum)} · Pro-Rata + Promote Breakdown:{' '}
              {fmtExact(investorLpGpSum)}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}
